import { SubmitHandler, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  Stack,
  Text,
  Flex,
  Input,
  Button,
  HookFormErrorMessage,
  IconTooltip,
} from '@pairy/pds-react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { theme } from '@pairy/styles';

import { Nullable } from '@/types/helper';
import { Client } from '@/components/User/_components/common/CreateFileModal/index.atom';
import useClientCheck from '@/hooks/queries/client/useClientCheck';
import {
  CLIENT_IDENTITY_REQUIRED_MESSAGE,
  FORM_VALIDATIONS,
} from '@/constants/formValidations';
import { formatPhone, stripPhone } from '@/utils/formatPhone';

const RequiredNotice = styled.div`
  padding: 8px 12px;
  background: ${theme.colors.zinc_50};
  border-radius: 4px;
  border: 1px solid ${theme.colors.zinc_200};
`;

const Textarea = styled.textarea<{ isError?: boolean }>`
  width: 100%;
  padding: 8px 16px;
  border-radius: 4px;
  resize: vertical;
  min-height: 80px;
  font-size: ${theme.fontSize.md};
  line-height: ${theme.lineHeight.md};
  font-weight: ${theme.fontWeight.regular};

  &::placeholder {
    color: ${theme.colors.zinc_400};
    font-weight: 300;
  }

  ${({ isError }) =>
    isError
      ? css`
          border: 1px solid ${theme.colors.cerise_500};
          background: ${theme.colors.cerise_50};
          color: ${theme.colors.zinc_500};
        `
      : css`
          border: 1px solid ${theme.colors.zinc_200};
          background: ${theme.colors.zinc_50};
          color: ${theme.colors.zinc_500};
        `}
`;

interface Inputs {
  name: string;
  email: string;
  company: string;
  phone: string;
  position: string;
  department: string;
  memo: string;
}

interface Props {
  addClient: (client: Client) => void;
  clients: Nullable<Client[]>;
  clientCount?: number;
  planLimitLabel?: string;
  isAtLimit?: boolean;
  onLimitExceeded?: () => void;
}

const NewClient = ({
  addClient,
  clients,
  clientCount,
  planLimitLabel,
  isAtLimit,
  onLimitExceeded,
}: Props) => {
  const {
    register,
    handleSubmit,
    setError,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm<Inputs>();

  const email = watch('email');
  const name = watch('name');
  const phone = watch('phone');
  const company = watch('company');

  const { refetch } = useClientCheck(
    {
      email,
    },
    {
      enabled: false,
      select: (data) => data.data.data,
      retry: false,
      onSuccess: () => {
        const values = getValues();

        addClient({
          id: uuidv4(),
          name: values.name || null,
          email: values.email || null,
          company: values.company || null,
          phone: values.phone ? stripPhone(values.phone) : null,
          position: values.position || null,
          department: values.department || null,
          memo: values.memo || null,
          isSelected: false,
        });

        reset();
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          setError('email', {
            message: error?.response.data.message,
          });
          return;
        }

        setError('email', {
          message: '이메일을 확인해주세요.',
        });
      },
    },
  );

  const onSubmit: SubmitHandler<Inputs> = async ({
    name,
    email,
    company,
    phone,
    position,
    department,
    memo,
  }) => {
    if (isAtLimit) {
      onLimitExceeded?.();
      return;
    }

    // 서버 정책 완화: 이메일만 입력해도 등록할 수 있다.
    if (!name && !phone && !company && !email) {
      setError('name', {
        type: 'required',
        message: CLIENT_IDENTITY_REQUIRED_MESSAGE,
      });
      return;
    }

    const isDuplicatedEmail =
      email && clients?.some((client) => client.email === email);
    if (isDuplicatedEmail) {
      setError('email', {
        message: '이미 존재하는 이메일입니다.',
      });
      return;
    }

    if (!email) {
      addClient({
        id: uuidv4(),
        name: name || null,
        email: null,
        company: company || null,
        phone: phone ? stripPhone(phone) : null,
        position: position || null,
        department: department || null,
        memo: memo || null,
        isSelected: false,
      });

      reset();
      return;
    }

    refetch();
  };

  return (
    <Stack
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      direction="column"
      gap={16}
    >
      {clientCount !== undefined && planLimitLabel && (
        <Flex justifyContent="flex-end">
          <Stack display="inline-flex" gap={8}>
            <Text variant="md-medium" color="zinc_500">
              {clientCount.toLocaleString()} / {planLimitLabel}
            </Text>
            <IconTooltip
              iconProps={{
                icon: 'ph:question-fill',
                size: 17,
                color: 'zinc_400',
              }}
            >
              - Free 플랜은 고객을 10명까지 등록할 수 있습니다.
              <br />- Basic 플랜은 고객을 100명까지 등록할 수 있습니다.
              <br />- Pro 플랜은 고객을 1,000명까지 등록할 수 있습니다.
              <br />- Pro+ 플랜은 고객을 10,000명까지 등록할 수 있습니다.
            </IconTooltip>
          </Stack>
        </Flex>
      )}
      <Stack gap={16} direction="column">
        <RequiredNotice>
          <Text variant="sm-regular" color="zinc_400">
            {`* ${CLIENT_IDENTITY_REQUIRED_MESSAGE}`}
          </Text>
        </RequiredNotice>
        <div>
          <label>
            <Flex alignItems="center">
              <Text
                as="p"
                variant="md-semibold"
                marginBottom={8}
                marginRight={2}
              >
                고객 이름
              </Text>
              <Text
                as="p"
                variant="sm-regular"
                color="cerise_500"
                marginBottom={8}
              >
                *
              </Text>
            </Flex>
            <Input
              type="text"
              width="100%"
              placeholder="자료를 받아볼 고객을 입력해 주세요."
              isError={Boolean(errors.name)}
              {...register('name')}
            />
          </label>
          <HookFormErrorMessage errors={errors} name="name" />
        </div>
        <div>
          <label>
            <Flex alignItems="center">
              <Text
                as="p"
                variant="md-semibold"
                marginBottom={8}
                marginRight={2}
              >
                회사명
              </Text>
              <Text
                as="p"
                variant="sm-regular"
                color="cerise_500"
                marginBottom={8}
              >
                *
              </Text>
            </Flex>
            <Input
              type="text"
              width="100%"
              placeholder="회사명을 입력해 주세요."
              isError={Boolean(errors.company)}
              {...register('company')}
            />
          </label>
          <HookFormErrorMessage errors={errors} name="company" />
        </div>
        <div>
          <label>
            <Flex alignItems="center">
              <Text
                as="p"
                variant="md-semibold"
                marginBottom={8}
                marginRight={2}
              >
                연락처
              </Text>
              <Text
                as="p"
                variant="sm-regular"
                color="cerise_500"
                marginBottom={8}
              >
                *
              </Text>
            </Flex>
            <Input
              type="text"
              width="100%"
              placeholder="예) 010-1234-5678"
              isError={Boolean(errors.phone)}
              {...register('phone', {
                onChange: (e) => {
                  setValue('phone', formatPhone(e.target.value), {
                    shouldValidate: true,
                  });
                },
              })}
            />
          </label>
          <HookFormErrorMessage errors={errors} name="phone" />
        </div>
        <div>
          <label>
            <Text as="p" variant="md-semibold" marginBottom={8} marginRight={4}>
              고객 이메일
            </Text>
            <Input
              type="text"
              width="100%"
              placeholder="예) example@salesclue.io"
              isError={Boolean(errors.email)}
              {...register('email', {
                pattern: {
                  value: FORM_VALIDATIONS.email.pattern.value,
                  message: FORM_VALIDATIONS.email.pattern.message,
                },
              })}
            />
          </label>
          <HookFormErrorMessage errors={errors} name="email" />
        </div>
        <div>
          <label>
            <Text as="p" variant="md-semibold" marginBottom={8}>
              직책
            </Text>
            <Input
              type="text"
              width="100%"
              placeholder="직책을 입력해 주세요."
              isError={Boolean(errors.position)}
              {...register('position')}
            />
          </label>
          <HookFormErrorMessage errors={errors} name="position" />
        </div>
        <div>
          <label>
            <Text as="p" variant="md-semibold" marginBottom={8}>
              부서
            </Text>
            <Input
              type="text"
              width="100%"
              placeholder="부서를 입력해 주세요."
              isError={Boolean(errors.department)}
              {...register('department')}
            />
          </label>
          <HookFormErrorMessage errors={errors} name="department" />
        </div>
        <div>
          <label>
            <Text as="p" variant="md-semibold" marginBottom={8}>
              메모
            </Text>
            <Textarea
              placeholder="메모를 입력해 주세요. (최대 500자)"
              isError={Boolean(errors.memo)}
              {...register('memo', {
                maxLength: {
                  value: FORM_VALIDATIONS.memo.maxLength.value,
                  message: FORM_VALIDATIONS.memo.maxLength.message,
                },
              })}
            />
          </label>
          <HookFormErrorMessage errors={errors} name="memo" />
        </div>
      </Stack>
      <Flex justifyContent="flex-end">
        <Button
          type="submit"
          variant="skyblue_400"
          radius={4}
          width="152px"
          textVariant="md-medium"
          disabled={!name && !phone && !company && !email}
        >
          새로운 고객 생성하기
        </Button>
      </Flex>
    </Stack>
  );
};

export default NewClient;
